import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Play, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { usePermissions, getPermissionTooltip } from '../hooks/usePermissions';
import { useToast } from '../components/ui/Toast';

export function AgentsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const { can, role } = usePermissions();
  const canRunAgents = can('agents:write');

  const { data: agentsData, isLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: () => api.getAgents(),
  });

  const { data: runsData, isLoading: runsLoading } = useQuery({
    queryKey: ['agentRuns'],
    queryFn: () => api.getAgentRuns(),
  });

  const runAgentMutation = useMutation({
    mutationFn: (agentType: string) => api.runAgent(agentType),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['agentRuns'] });
      queryClient.invalidateQueries({ queryKey: ['recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['alertCount'] });

      const count = data.recommendationsCreated ?? 0;
      toast.success(
        `Analysis complete! ${count} recommendations generated.`,
        count > 0 ? { label: 'View Recommendations \u2192', onClick: () => navigate('/recommendations') } : undefined,
      );
    },
    onError: (error: any, _agentType) => {
      toast.error(
        `Analysis failed: ${error.response?.data?.message ?? error.message}`,
      );
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const agents = agentsData?.agents ?? [];
  const recentRuns = runsData?.data?.slice(0, 5) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analysis</h1>
        <p className="text-gray-600">
          Run AI-powered analysis on your financial data to generate actionable recommendations
        </p>
      </div>

      {/* Viewer notice */}
      {!canRunAgents && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
          {getPermissionTooltip(role)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((agent: any) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onRun={() => runAgentMutation.mutate(agent.id)}
            isRunning={runAgentMutation.isPending && runAgentMutation.variables === agent.id}
            disabled={!canRunAgents}
            disabledReason={!canRunAgents ? getPermissionTooltip(role) : undefined}
          />
        ))}
      </div>

      {/* Recent Runs */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Analyses</h2>
        {runsLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : recentRuns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-2 font-medium">Analysis Type</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Recommendations</th>
                  <th className="pb-2 font-medium">Credits</th>
                  <th className="pb-2 font-medium">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentRuns.map((run: any) => (
                  <tr key={run.id} className="text-sm">
                    <td className="py-3">
                      <div className="flex items-center space-x-2">
                        <Sparkles className="h-4 w-4 text-legacy-600" />
                        <span className="font-medium capitalize">{run.agentType}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      <StatusBadge status={run.status} />
                    </td>
                    <td className="py-3 text-gray-600">
                      {run.recommendationsCreated ?? 0}
                    </td>
                    <td className="py-3 text-gray-600">
                      {run.creditsUsed ?? 0}
                    </td>
                    <td className="py-3 text-gray-500">
                      {new Date(run.startedAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={Sparkles}
            title="No analyses yet"
            description="Run your first analysis to generate personalized recommendations for your wealth strategy."
          />
        )}
      </div>
    </div>
  );
}

interface AgentCardProps {
  agent: {
    id: string;
    name: string;
    description: string;
    creditCost: { base: number; max: number };
  };
  onRun: () => void;
  isRunning: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

function AgentCard({ agent, onRun, isRunning, disabled, disabledReason }: AgentCardProps) {
  // All agents returned from API are implemented and runnable
  // But check permission-based disable as well
  const canRun = !isRunning && !disabled;

  return (
    <div className="card flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 bg-legacy-100 rounded-lg">
          <Sparkles className="h-6 w-6 text-legacy-600" />
        </div>
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Available
        </span>
      </div>

      <h3 className="font-semibold text-gray-900">{agent.name}</h3>
      <p className="text-sm text-gray-600 mt-1 flex-1">{agent.description}</p>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-gray-500">
          {agent.creditCost.base}-{agent.creditCost.max} credits
        </span>
        <button
          onClick={onRun}
          disabled={!canRun}
          title={disabledReason}
          className={clsx(
            'flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
            canRun
              ? 'bg-legacy-600 text-white hover:bg-legacy-700'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          )}
        >
          {isRunning ? (
            <>
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-1.5" />
              Run Analysis
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    completed: 'bg-green-100 text-green-800',
    running: 'bg-blue-100 text-blue-800',
    failed: 'bg-red-100 text-red-800',
    pending: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status] ?? colors.pending}`}>
      {status}
    </span>
  );
}
